"use client"

import { sessionCookieFetch } from '@sourcepilot/ui/framework/data/dag-sync/organisation-change';

import { useCallback, useEffect } from 'react'
import { servingIdentityOf, setServingIdentity, servingIdentityIsNodeId } from '../../framework/data/dag-sync/serving-identity'
import { forgetPackCachesAtSignOut } from '../../framework/data/state-packs/pack-cache'
import { forgetIslandStoresAtSignOut, forgetRemovedOrganisations } from '../../framework/data/island-store/island-store'
import { atom, useAtomValue, useSetAtom } from 'jotai'

const SOURCEPILOT_SERVER_URL = process.env.NEXT_PUBLIC_SOURCEPILOT_URL

interface User {
  identifier: string
  /** The caller's User NODE id (from /auth/user) — address "me" precisely;
   *  never infer identity from list queries (admin mode returns everyone). */
  user_node_id?: string
  metadata: {
    image?: string
    name?: string
    [key: string]: any
  }
  display_name?: string
  is_admin?: boolean
  admin_mode?: boolean
  admin_write_mode?: boolean
  // Impersonation state
  impersonating?: boolean
  /** the user this session views as: their public profile, never their
   *  sign-in identifier (VC-Authz 9db79b501) */
  impersonated_person?: { id: string; name?: string | null; avatar?: string | null } | null
  impersonate_write?: boolean
}

interface AuthConfig {
  requireLogin: boolean
  passwordAuth: boolean
  headerAuth: boolean
  oauthProviders: string[]
}

interface UseAuthReturn {
  user: User | null
  isLoading: boolean
  authConfig: AuthConfig | null
  logout: () => Promise<void>
  refetch: () => Promise<void>
}

// Global auth state atoms
export const userAtom = atom<User | null>(null)
const authConfigAtom = atom<AuthConfig | null>(null)
const isLoadingAtom = atom<boolean>(true)
const lastCheckAtom = atom<number>(0) // Timestamp of last successful auth check
export const isLoggingOutAtom = atom<boolean>(false) // Prevents refetch during logout

/**
 * Standalone logout function that can be called from anywhere
 * Handles session clearing, API call, and redirect
 */
export async function performLogout() {
  try {
    // Store current URL before clearing session storage (so user returns here after re-login)
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : null

    // the signed-out user's stored state packs and island state go before
    // the page leaves (B4: deleted at sign-out until they are encrypted;
    // stage 2: other tabs are told, and reload)
    await Promise.all([forgetPackCachesAtSignOut(), forgetIslandStoresAtSignOut()])

    // Clear session storage
    sessionStorage.clear()

    // Restore return URL after clearing (if not on login page)
    if (currentPath && currentPath !== '/login' && currentPath !== '/login/callback') {
      sessionStorage.setItem('returnUrl', currentPath)
    }

    // Call logout endpoint (clears server session and auth_status cookie)
    await sessionCookieFetch(`${SOURCEPILOT_SERVER_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store',
    })
  } catch (error) {
    console.error('[performLogout] Logout error:', error)
  } finally {
    // Always redirect to login, even if logout fails
    if (typeof window !== 'undefined') {
      window.location.href = '/login'
    }
  }
}

// ────────────────────────────────────────────────────────────────
// Module-scoped fetch + dedup state
// ────────────────────────────────────────────────────────────────
//
// The actual fetch lives at module scope (not inside the hook closure)
// so we can dedup across components. Without this, every consumer of
// `useSimpleAuth` that mounts fires its own `fetchAuth` from its own
// `useEffect` — under Suspense thrashing on initial load that fans
// out into dozens of duplicate /auth/config + /auth/user requests
// (we counted 76 in 12s on prod 2026-06-01 before this refactor).
//
// Setters are wired in once by `useAuthInit` (called by AppProviders).
// All other consumers use the read-only `useSimpleAuth` which has no
// side effects — they just subscribe to the atoms.

type Setters = {
  setUser: (u: User | null) => void
  setAuthConfig: (c: AuthConfig | null) => void
  setIsLoading: (b: boolean) => void
}

let registeredSetters: Setters | null = null
let inFlight: Promise<void> | null = null

/** Exported for tests. Only a 401 says "signed out". A server that cannot be
 *  reached (a deploy, a restart, the network) says nothing about the
 *  session: it used to null the user, and the session watchdog's next poll
 *  then sent every open tab to the sign-in page mid-outage, losing the page
 *  (VC-e2e's browser restart check, 2026-10-07). */
export async function doFetchAuth(setters: Setters): Promise<void> {
  try {
    // Fetch auth config and user in parallel
    const [configRes, userRes] = await Promise.all([
      sessionCookieFetch(`${SOURCEPILOT_SERVER_URL}/auth/config`, {
        credentials: 'include',
        cache: 'no-store',  // Never cache auth checks
      }),
      sessionCookieFetch(`${SOURCEPILOT_SERVER_URL}/auth/user`, {
        credentials: 'include',
        cache: 'no-store',  // Never cache auth checks
      }),
    ])

    if (configRes.ok) {
      const config = await configRes.json()
      setters.setAuthConfig(config)
    }

    if (userRes.ok) {
      const userData = await userRes.json()
      setters.setUser(userData)
      // what the sync client keeps between opens is kept per identity
      setServingIdentity(servingIdentityOf(userData), {
        impersonating: !!userData?.impersonating,
        byNodeId: servingIdentityIsNodeId(userData),
      })
      // once per page: what this browser keeps for organisations the user
      // has left goes (client caching stage 2)
      void forgetRemovedOrganisations(async () => {
        const r = await sessionCookieFetch(`${SOURCEPILOT_SERVER_URL}/auth/organisations`, { credentials: 'include', cache: 'no-store' })
        if (!r.ok) return null
        const body = await r.json()
        return Array.isArray(body?.organisations) ? body.organisations.map((o: { id: string }) => o.id) : null
      })
    } else if (userRes.status === 401) {
      setters.setUser(null)
      setServingIdentity(null)
    }
  } catch (error) {
    // unreachable, not unauthenticated: keep the user and config we have
    console.warn('[useSimpleAuth] Auth check could not reach the server; keeping the session as it was:', error)
  } finally {
    setters.setIsLoading(false)
  }
}

/**
 * Trigger an auth fetch. Dedups concurrent callers — if a fetch is
 * already in flight, returns the same promise. Skips silently if
 * setters haven't been registered yet (no `useAuthInit` mounted).
 *
 * `force = true` waits for any in-flight fetch then starts a fresh
 * one. Used by `refetch()` after explicit state changes (e.g.
 * impersonation toggle) where the in-flight result would be stale.
 *
 * Used internally by `useAuthInit` for initial fetch and exposed
 * via `useSimpleAuth().refetch` for explicit re-fetches.
 */
async function fetchAuthDeduped(force = false): Promise<void> {
  if (!registeredSetters) return
  if (inFlight && !force) return inFlight
  if (inFlight && force) {
    // Wait for the in-flight one to finish, then start fresh below
    try { await inFlight } catch { /* ignored — we'll fetch fresh */ }
  }
  inFlight = doFetchAuth(registeredSetters)
  try {
    await inFlight
  } finally {
    inFlight = null
  }
}

/**
 * Owns the auth-fetch lifecycle. Call this ONCE at the top of the
 * app (in `AppProviders`). All other components should use
 * `useSimpleAuth()` instead — that's a pure read of the atoms with
 * no side effects.
 *
 * Wires setters into the module-scoped dedup state on mount, then
 * fires the initial fetch. On unmount, clears the registration so
 * a subsequent mount can re-register cleanly.
 */
export function useAuthInit(): void {
  const setUser = useSetAtom(userAtom)
  const setAuthConfig = useSetAtom(authConfigAtom)
  const setIsLoading = useSetAtom(isLoadingAtom)
  const isLoggingOut = useAtomValue(isLoggingOutAtom)

  useEffect(() => {
    registeredSetters = { setUser, setAuthConfig, setIsLoading }
    // Don't fire if logging out (preserves the original race-protection)
    if (!isLoggingOut) {
      fetchAuthDeduped()
    }
    return () => {
      registeredSetters = null
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Read-only auth state hook. Use this in any component that needs
 * `user`, `isLoading`, `authConfig`, or wants to call `logout` /
 * `refetch`. Has NO side effects on mount — it subscribes to Jotai
 * atoms whose values are written by `useAuthInit` in AppProviders.
 *
 * Safe to call from N components without fan-out (the previous
 * implementation fired a fetch per-consumer-per-mount; under
 * Suspense thrashing this multiplied into dozens of duplicate
 * requests during initial load).
 */
export function useSimpleAuth(): UseAuthReturn {
  const user = useAtomValue(userAtom)
  const authConfig = useAtomValue(authConfigAtom)
  const isLoading = useAtomValue(isLoadingAtom)
  const setIsLoggingOut = useSetAtom(isLoggingOutAtom)

  const logout = useCallback(async () => {
    // Set flag first to prevent any auth refetches during logout
    setIsLoggingOut(true)
    // Don't setUser(null) here! It triggers AuthLoader to refetch /auth/user
    // before the logout completes, causing a race condition.
    // performLogout() handles everything including redirect.
    await performLogout()
  }, [setIsLoggingOut])

  const refetch = useCallback(async () => {
    await fetchAuthDeduped(/* force */ true)
  }, [])

  return {
    user,
    isLoading,
    authConfig,
    logout,
    refetch,
  }
}
